import { isAxiosError } from 'axios';
import { OFFLINE_MUTATION_CODE } from './api-error-code';
import { t } from './i18n';

// The auth service speaks English in its HTTPException details
// ("Incorrect password", "User already exists", …). Surface friendly
// Russian copy for the auth flow instead of leaking raw server strings to
// paying users on their very first impression.
export function authErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    // Wrong login OR wrong password — collapse 401/404 into one message so we
    // neither leak English nor reveal which accounts exist.
    if (status === 401 || status === 404) return t('auth.bad_credentials');
    // Login already taken on registration.
    if (status === 409) return t('auth.login_taken');
    // Network / server down — give actionable Russian copy, not "Network Error".
    if (status === undefined || status >= 500) return t('auth.server_unreachable');
  }
  return apiErrorMessage(err, fallback);
}

export function tgAuthErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401) return t('auth.login_failed');
    if (status === 503) return t('auth.server_unreachable');
    if (status === undefined || status >= 500) return t('auth.server_unreachable');
  }
  return t('auth.login_failed');
}

function planGateMessage(detail: string): string | null {
  const m = /^Upgrade required:\s*(\w+)/i.exec(detail.trim());
  if (!m) return null;
  const plan = m[1].toLowerCase();
  const known = t(`billing.plan.${plan}`);
  const label = known === `billing.plan.${plan}` ? plan.charAt(0).toUpperCase() + plan.slice(1) : known;
  return t('billing.upgrade_required_plan', { plan: label });
}

function knownDetailMessage(detail: string): string | null {
  const providerMessages: Record<string, string> = {
    provider_unconfigured: t('nutr.provider_status.unconfigured'),
    provider_rate_limited: t('nutr.provider_status.rate_limited'),
    provider_offline: t('nutr.provider_status.offline'),
    provider_unavailable: t('nutr.provider_status.unavailable'),
    provider_food_incomplete: t('nutr.provider_food_incomplete'),
  };
  if (providerMessages[detail]) return providerMessages[detail];
  if (/AI data processing consent required/i.test(detail)) {
    return t('coach.ai_consent_required');
  }
  return planGateMessage(detail);
}

function userFacingDetail(detail: string): string | null {
  if (!/[\u0400-\u04ff]/u.test(detail)) return null;
  if (/^[a-z\d]+(?:[_-][a-z\d]+)+$/i.test(detail)) return null;
  return detail;
}

function safeServerText(value: string): string | null {
  const printable = Array.from(value, (character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127 ? ' ' : character;
  }).join('');
  const normalized = printable.replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length > 500) return null;
  if (/<\/?[a-z][^>]*>|<!doctype|&(?:lt|gt|nbsp|#\d+);/i.test(normalized)) return null;
  if (/(?:traceback \(|stack trace|\b(?:authorization|bearer|password|secret|token|api[_-]?key)\s*[:=]|[a-z]:\\[^\s]+:\d+)/i.test(normalized)) return null;
  return normalized;
}

function nestedDetail(value: unknown, depth = 0): string | null {
  if (depth > 4) return null;
  if (typeof value === 'string') return safeServerText(value);
  if (Array.isArray(value)) {
    for (const entry of value) {
      const candidate = nestedDetail(entry, depth + 1);
      if (candidate) return candidate;
    }
    return null;
  }
  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  for (const key of ['detail', 'message', 'msg', 'error', 'errors']) {
    const candidate = nestedDetail(record[key], depth + 1);
    if (candidate) return candidate;
  }
  return null;
}

function transportErrorMessage(code: string | undefined): string | null {
  if (code === OFFLINE_MUTATION_CODE) return 'Нет подключения. Изменение не сохранено.';
  if (code === 'ERR_CANCELED') return 'Запрос отменён.';
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 'Сервер не ответил вовремя. Повтори попытку.';
  }
  if (code === 'ERR_NETWORK') return 'Нет связи с сервером. Проверь подключение.';
  return null;
}

export function apiErrorMessage(err: unknown, fallback = t('common.something_broke')): string {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    const transportMessage = transportErrorMessage(err.code);
    if (transportMessage) return transportMessage;
    if (status === undefined) {
      return typeof navigator !== 'undefined' && navigator.onLine === false
        ? 'Нет подключения к интернету.'
        : t('auth.server_unreachable');
    }
    const data = err.response?.data;
    const responseDetail = nestedDetail(data);
    if (responseDetail) {
      const known = knownDetailMessage(responseDetail);
      if (known) return known;
    }
    if (status >= 500) return t('common.something_broke');
    return responseDetail ? userFacingDetail(responseDetail) ?? fallback : fallback;
  }
  return fallback;
}
